import { useState, useRef, useEffect } from "react";
import LocalLink from "@/components/LocalLink";
import { ChevronDown, Home, Briefcase, Scale, Shield } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

interface MegaCategory {
  icon: React.ElementType;
  headingKey: string;
  links: { labelKey: string; href: string }[];
}

const categories: MegaCategory[] = [
  {
    icon: Home,
    headingKey: "mega.family.heading",
    links: [
      { labelKey: "mega.family.petitions", href: "/services/family-immigration" },
      { labelKey: "mega.family.greenCards", href: "/services/green-cards" },
      { labelKey: "mega.family.naturalization", href: "/services/citizenship" },
    ],
  },
  {
    icon: Briefcase,
    headingKey: "mega.business.heading",
    links: [
      { labelKey: "mega.business.eb2", href: "/services/business-talent-visas" },
      { labelKey: "mega.business.o1", href: "/services/o1-visa" },
      { labelKey: "mega.business.investor", href: "/services/e2-investor-visa" },
      { labelKey: "mega.business.h1b", href: "/services/business-talent-visas" },
    ],
  },
  {
    icon: Scale,
    headingKey: "mega.courtroom.heading",
    links: [
      { labelKey: "mega.courtroom.deportation", href: "/services/deportation" },
      { labelKey: "mega.courtroom.detention", href: "/services/ice-detention-bond-hearings" },
      { labelKey: "crim.nav.label", href: "/services/crimmigration" },
      { labelKey: "mega.courtroom.vawa", href: "/services/u-visa-vawa" },
      { labelKey: "mega.courtroom.waivers", href: "/services/waivers" },
    ],
  },
  {
    icon: Shield,
    headingKey: "mega.criminal.heading",
    links: [
      { labelKey: "mega.criminal.dui", href: "/services/dui-dwi-defense" },
      { labelKey: "mega.criminal.drugs", href: "/services/drug-defense" },
    ],
  },
];

const PracticeAreasDropdown = ({ mobile = false, onNavigate }: { mobile?: boolean; onNavigate?: () => void }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openCategory, setOpenCategory] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { t } = useLanguage();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleMouseEnter = () => {
    if (mobile) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    if (mobile) return;
    timeoutRef.current = setTimeout(() => setIsOpen(false), 250);
  };

  // ─── MOBILE: Accordion ───
  if (mobile) {
    return (
      <div>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center justify-between w-full min-h-[48px] py-2 pl-2 text-base text-white font-medium hover:text-gold transition-colors"
          aria-expanded={isOpen}
        >
          {t("nav.practiceAreas")}
          <ChevronDown
            className={`w-4 h-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          />
        </button>
        {isOpen && (
          <div className="mt-2 mb-3 ml-2 space-y-1 rounded-lg bg-white/[0.03] p-2 motion-safe:animate-fade-in">
            {categories.map((cat, idx) => {
              const Icon = cat.icon;
              const isCatOpen = openCategory === idx;
              return (
                <div key={cat.headingKey}>
                  <button
                    onClick={() => setOpenCategory(isCatOpen ? null : idx)}
                    className="flex items-center justify-between w-full min-h-[44px] py-2 px-2 rounded-md text-white/90 hover:bg-white/5 transition-colors border-b border-white/5"
                  >
                    <span className="flex items-center gap-2 text-sm">
                      <Icon className="w-4 h-4 text-gold" />
                      {t(cat.headingKey)}
                    </span>
                    <ChevronDown
                      className={`w-3.5 h-3.5 text-white/50 transition-transform duration-200 ${
                        isCatOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {isCatOpen && (
                    <div className="ml-6 space-y-0.5 pb-1 motion-safe:animate-fade-in">
                      {cat.links.map((link) => (
                        <LocalLink
                          key={link.labelKey}
                          to={link.href}
                          onClick={() => { setIsOpen(false); onNavigate?.(); }}
                          className="flex items-center min-h-[44px] py-1.5 text-sm text-white/70 hover:text-gold transition-colors"
                        >
                          {t(link.labelKey)}
                        </LocalLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <LocalLink
              to="/practice-areas"
              onClick={() => { setIsOpen(false); onNavigate?.(); }}
              className="block text-gold text-sm font-semibold hover:text-gold-light transition-colors pt-2 mt-1 border-t border-white/10 px-2"
            >
              {t("mega.viewAll")} →
            </LocalLink>
          </div>
        )}
      </div>
    );
  }

  // ─── DESKTOP: Mega Menu ───
  return (
    <div
      ref={dropdownRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        className="flex items-center gap-1 text-sm font-medium text-white/80 hover:text-gold transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        {t("nav.practiceAreas")}
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[720px] bg-navy/95 backdrop-blur-2xl rounded-xl shadow-[0_16px_48px_rgba(0,0,0,0.35)] border border-white/10 overflow-hidden animate-fade-in z-50">
          {/* Top accent line */}
          <div className="h-[2px] bg-gradient-to-r from-transparent via-gold to-transparent" />

          <div className="grid grid-cols-4 gap-0 p-6">
            {categories.map((cat) => {
              const Icon = cat.icon;
              return (
                <div key={cat.headingKey} className="px-3">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 rounded-md bg-gold/10 flex items-center justify-center">
                      <Icon className="w-3.5 h-3.5 text-gold" />
                    </div>
                    <h3 className="text-[11px] font-bold uppercase tracking-widest text-gold">
                      {t(cat.headingKey)}
                    </h3>
                  </div>
                  <div className="space-y-0.5">
                    {cat.links.map((link) => (
                      <LocalLink
                        key={link.labelKey}
                        to={link.href}
                        onClick={() => { setIsOpen(false); onNavigate?.(); }}
                        className="block px-2 py-1.5 text-[13px] text-white/70 hover:text-white hover:bg-white/5 rounded-md transition-colors"
                      >
                        {t(link.labelKey)}
                      </LocalLink>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="bg-white/5 px-6 py-3 border-t border-white/10">
            <LocalLink
              to="/practice-areas"
              onClick={() => { setIsOpen(false); onNavigate?.(); }}
              className="text-sm font-semibold text-gold hover:text-gold-light transition-colors flex items-center gap-1"
            >
              {t("mega.viewAll")} →
            </LocalLink>
          </div>
        </div>
      )}
    </div>
  );
};

export default PracticeAreasDropdown;
